import { useMemo } from 'react';
import type { ActiveContext } from '@pc/db';
import { MissingActiveContextError } from '@pc/core';

/**
 * Calendar's context adapter intentionally accepts a NULL `storeId` so the
 * module's pages render under the org-mode shell ("Enter organization")
 * with no store selected. The downstream service layer
 * (`packages/db/src/services/calendar.ts`) already branches on
 * `if (storeId) { ... }` and returns every appointment in the org (all
 * stores + org-wide + user-scoped) when `storeId` is null -- this is
 * exactly the agenda an operator wants when entering the org workspace.
 *
 * `orgId` is still strictly required: without an org we cannot filter the
 * agenda at all. Throwing `MissingActiveContextError` bounces the user to
 * `/choose-context` via the router-level error element.
 */
export function useActiveCtx(): ActiveContext {
  if (typeof window === 'undefined') {
    throw new Error('useActiveCtx requires a browser environment.');
  }
  const orgId = window.localStorage.getItem('pc.activeOrgId');
  const storeId = window.localStorage.getItem('pc.activeStoreId');
  // Memoize so the returned reference is stable per (orgId, storeId)
  // pair. Without this, every render allocates a fresh object and any
  // caller using `ctx` directly as a useEffect dep re-fires the effect
  // every render, looping the underlying RPCs.
  const ctx = useMemo<ActiveContext | null>(
    () => (orgId ? { orgId, storeId: storeId ?? null } : null),
    [orgId, storeId],
  );
  if (!ctx) {
    throw new MissingActiveContextError({ orgId: true, storeId: !storeId });
  }
  return ctx;
}
